"use client"

import { motion } from "motion/react"
import { Activity, Box, Calendar, Database, Layers, Shield } from "lucide-react"
import Link from "next/link"

import { Button } from "@/core/ui/button"
import Beams from "@/core/ui/effects/Beams"
import SplitText from "@/core/ui/effects/SplitText"
import { ServiceTheme } from "@/core/utils/service-theme"

interface SubServiceHeroProps {
    title: string
    description: string
    icon?: string
    categoryTitle: string
    theme: ServiceTheme
}

const HeroGraphic = ({ category, theme }: { category: string, theme: ServiceTheme }) => {
    // Blockchain: Rotating Blocks/Cube
    if (category.toLowerCase().includes('blockchain')) {
        return (
            <div className="relative w-64 h-64 md:w-96 md:h-96">
                <div className={`absolute inset-0 border-2 border-dashed ${theme.accentBorder} rounded-3xl animate-[spin_20s_linear_infinite]`} />
                <div className={`absolute inset-4 border border-dashed ${theme.accentBorder} rounded-3xl animate-[spin_15s_linear_infinite_reverse] opacity-50`} />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-32 h-32">
                        <div className={`absolute inset-0 bg-${theme.accentColor.split('-')[1]}-500/20 blur-xl`} />
                        <Box className={`w-32 h-32 ${theme.accentColor} animate-pulse`} strokeWidth={1} />
                    </div>
                </div>
                {/* Floating elements */}
                <motion.div
                    animate={{ y: [-10, 10, -10] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className={`absolute top-0 right-0 p-3 rounded-xl bg-card border ${theme.accentBorder} shadow-lg`}
                >
                    <Database className={`w-6 h-6 ${theme.accentColor}`} />
                </motion.div>
                <motion.div
                    animate={{ y: [10, -10, 10] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className={`absolute bottom-0 left-0 p-3 rounded-xl bg-card border ${theme.accentBorder} shadow-lg`}
                >
                    <Shield className={`w-6 h-6 ${theme.accentColor}`} />
                </motion.div>
            </div>
        )
    }

    // GenAI: Neural Orb
    if (category.toLowerCase().includes('genai')) {
        return (
            <div className="relative w-64 h-64 md:w-96 md:h-96 flex items-center justify-center">
                <div className={`absolute inset-0 rounded-full ${theme.accentBg} blur-[80px] opacity-40`} style={{ backgroundColor: theme.hex }} />
                <div className="relative z-10 grid grid-cols-3 gap-4">
                    {[...Array(9)].map((_, i) => (
                        <motion.div
                            key={i}
                            initial={{ scale: 0.5, opacity: 0.5 }}
                            animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.5, 1, 0.5] }}
                            transition={{
                                // eslint-disable-next-line react-hooks/purity
                                duration: 2 + Math.random(),
                                repeat: Infinity,
                                delay: i * 0.2
                            }}
                            className={`w-4 h-4 rounded-full ${theme.accentBg} border ${theme.accentBorder}`}
                            style={{ backgroundColor: theme.hex }}
                        />
                    ))}
                </div>
                <svg className="absolute inset-0 w-full h-full opacity-30 animate-[spin_60s_linear_infinite]">
                    <circle cx="50%" cy="50%" r="40%" stroke={theme.hex} strokeWidth="1" fill="none" strokeDasharray="4 4" />
                    <circle cx="50%" cy="50%" r="30%" stroke={theme.hex} strokeWidth="1" fill="none" />
                </svg>
            </div>
        )
    }

    // Enterprise: City/Grid
    if (category.toLowerCase().includes('enterprise')) {
        return (
            <div className="relative w-64 h-64 md:w-96 md:h-96">
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-blue-500/20 to-transparent" />
                <div className="grid grid-cols-4 gap-2 h-full items-end p-8">
                    {[40, 70, 50, 80].map((height, i) => (
                        <motion.div
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${height}%` }}
                            transition={{ duration: 1, delay: i * 0.1 }}
                            className={`w-full border ${theme.accentBorder} bg-card/50 backdrop-blur-xs relative group`}
                        >
                            <div className={`absolute top-0 left-0 w-full h-1 ${theme.accentBg}`} style={{ backgroundColor: theme.hex }} />
                            <div className="absolute inset-0 bg-grid-white/[0.1] dark:bg-grid-white/[0.1] bg-grid-black/[0.1]" />
                        </motion.div>
                    ))}
                </div>
                <Activity className={`absolute top-1/4 right-10 w-12 h-12 ${theme.accentColor} opacity-50`} />
            </div>
        )
    }

    // Development: Terminal/Code
    return (
        <div className="relative w-64 h-64 md:w-96 md:h-80 bg-card rounded-lg border border-border/20 overflow-hidden shadow-2xl">
            <div className="h-8 bg-muted/40 border-b border-border/10 flex items-center px-4 gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
                <div className="ml-4 text-[10px] font-mono text-muted-foreground">server.ts</div>
            </div>
            <div className="p-4 font-mono text-xs md:text-sm text-muted-foreground">
                <div className="mb-2"><span className="text-pink-500">import</span> {`{ ${theme.hex ? 'Energy' : 'Module'} }`} <span className="text-pink-500">from</span> <span className="text-green-400">'@system/core'</span></div>
                <div className="mb-2"><span className="text-blue-400">class</span> <span className="text-yellow-500 dark:text-yellow-300">SystemArch</span> <span className="text-pink-500">implements</span> <span className="text-yellow-500 dark:text-yellow-300">IProtocol</span> {`{`}</div>
                <div className="pl-4 mb-2">
                    <span className="text-pink-500">private</span> <span className="text-blue-400">status</span>: <span className="text-green-400">'active'</span>;
                </div>
                <div className="pl-4 mb-2">
                    <span className="text-pink-500">async</span> <span className="text-yellow-500 dark:text-yellow-300">deploy</span>() {`{`}
                </div>
                <div className="pl-8 mb-2">
                    <span className="text-muted-foreground/70">{"// Initializing sequence..."}</span>
                </div>
                <motion.div
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="pl-8 w-2 h-4 bg-emerald-500"
                />
                <div className="pl-4">{'}'}</div>
                <div>{'}'}</div>
            </div>
        </div>
    )
}

export default function SubServiceHero({ title, description, icon: _icon, categoryTitle, theme }: SubServiceHeroProps) {
    return (
        <div className="relative min-h-[70vh] w-full flex items-center overflow-hidden bg-background border-b border-border/20 transition-colors duration-300">
            {/* Background Layer */}
            <div className="absolute inset-0 z-0 opacity-40 mix-blend-multiply dark:mix-blend-normal">
                <Beams beamWidth={1.5} beamHeight={6} beamNumber={16} speed={0.8} lightColor={theme.hex} />
            </div>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 z-1 bg-linear-to-r from-background via-background/90 to-background/40 pointer-events-none" />

            {/* Content Layer */}
            <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-16 pt-24">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">

                    {/* Left Column: Text */}
                    <div className="max-w-3xl flex-1">
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="mb-8 inline-flex"
                        >
                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded border ${theme.accentBorder} ${theme.accentBg} backdrop-blur-md text-[10px] font-mono tracking-[0.2em] ${theme.accentColor} uppercase shadow-[0_0_15px_-5px_currentColor]`}>
                                <Layers className="w-3 h-3" />
                                {categoryTitle} / MODULE
                            </span>
                        </motion.div>

                        {/* Main Heading */}
                        <div className="mb-8 relative">
                            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.1]" style={{ textShadow: `0 0 40px ${theme.hex}30` }}>
                                <SplitText
                                    text={title}
                                    className="text-foreground"
                                    delay={30}
                                />
                            </h1>
                        </div>

                        {/* Description */}
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed border-l-2 border-border/20 pl-6"
                        >
                            {description}
                        </motion.p>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="flex flex-wrap gap-4 items-center"
                        >
                            <Button asChild size="lg" className={`rounded-full px-8 h-12 text-base font-medium text-white transition-all duration-300 shadow-[0_0_20px_-5px_rgba(255,255,255,0.1)] border-none hover:scale-105`} style={{ backgroundColor: theme.hex }}>
                                <Link href="/contact">
                                    Initialize Project
                                </Link>
                            </Button>
                            <Button asChild variant="ghost" size="lg" className="rounded-full px-8 h-12 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors border border-border/20">
                                <a
                                    href="https://cal.com/hamid-ayub/discovery"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2"
                                >
                                    <Calendar className="h-4 w-4" />
                                    Quick Call
                                </a>
                            </Button>
                        </motion.div>
                    </div>

                    {/* Right Column: Dynamic Graphic */}
                    <div className="hidden lg:flex flex-1 justify-center items-center perspective-1000">
                        <motion.div
                            initial={{ opacity: 0, rotateY: 20, scale: 0.8 }}
                            animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="relative"
                        >
                            <HeroGraphic category={categoryTitle} theme={theme} />
                        </motion.div>
                    </div>

                </div>
            </div>
        </div>
    )
}
